import { useCallback, useState } from 'react';

import getLatestCDLImage from '@/endpoints/schoolcraft/cdl/getLatestCDLImage';
import getLatestCDLStats from '@/endpoints/schoolcraft/cdl/getLatestCDLStats';
import { ICDLStatsResponse, ILayer } from '@/models/cdl';
import { calculateCropPercentages } from '@/utils/insights/cropHistory';
import Logger from '@/utils/logger';

import {
  ICropAcreage,
  ICropColors,
  ICropPercentage,
} from '../insights/cropHistory/summary';

export interface ICropHistoryData {
  stats: ICDLStatsResponse | null;
  image: ILayer | null;
}

export interface ICropPercentages {
  cropAcres: ICropAcreage | null;
  cropColors: ICropColors | null;
  cropPercentages: ICropPercentage | null;
}

export function useEmbedCrop(boundary) {
  const [loading, setLoading] = useState<boolean>(false);
  const [imagesLoading, setImagesLoading] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);
  const [cropHistoryData, setCropHistoryData] = useState<ICropHistoryData>({
    image: null,
    stats: null,
  });
  const [cropPercentages, setCropPercentages] = useState<ICropPercentages>({
    cropAcres: null,
    cropColors: null,
    cropPercentages: null,
  });

  const fetchCropStats = useCallback(async () => {
    if (!boundary) {
      return;
    }

    try {
      setLoading(true);
      const { res, err } = await getLatestCDLStats({
        boundary,
      });

      if (err) {
        throw Error(err.message);
      }

      if (!res?.data) {
        setCropHistoryData({
          image: null,
          stats: null,
        });
      } else {
        setCropHistoryData((prev) => {
          return {
            ...prev,
            stats: res.data.info,
          };
        });
        setCropPercentages(calculateCropPercentages([res.data.info]));
      }
    } catch (err) {
      Logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [boundary]);

  const fetchCropImages = useCallback(async () => {
    if (!boundary) {
      return;
    }

    try {
      setImagesLoading(true);
      const { res, err } = await getLatestCDLImage({
        boundary,
      });

      if (err) {
        Logger.error(err.message);
        setError(err);
      }

      if (!res?.data) {
        setCropHistoryData({
          image: null,
          stats: null,
        });
      } else {
        setCropHistoryData((prev) => {
          return {
            ...prev,
            image: res.data.info as unknown as ILayer | null,
          };
        });
      }
    } catch (err) {
      Logger.error(err);
    } finally {
      setImagesLoading(false);
    }
  }, [boundary]);

  const fetchCrop = useCallback(() => {
    fetchCropImages();
    fetchCropStats();
  }, [fetchCropImages, fetchCropStats]);

  return {
    cropHistoryData,
    cropPercentages,
    error,
    fetchData: fetchCrop,
    imagesLoading,
    loading,
  };
}
